import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import TrainingSummaryService from "../../../services/trainingSummaryService";

const OutlinePanel = ({ baseUrl, trainingId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [outlineData, setOutlineData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchOutline = async () => {
      if (!trainingId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await TrainingSummaryService.getTrainingSummary({ trainingId });
        setOutlineData(data);
      } catch (err) {
        console.error("Error fetching course outline:", err);
        setError(err.message || i18n.t("AudioPanel.ErrorLoadingData"));
      } finally {
        setLoading(false);
      }
    };

    fetchOutline();
  }, [trainingId]);

  const courseOutline = outlineData?.courseOutline;

  const copyToClipboard = async () => {
    if (!courseOutline) return;

    let text = "";

    // Course Info
    if (courseOutline.courseInfo) {
      text += `${i18n.t("AudioPanel.CourseInfo")}\n`;
      text += "=".repeat(50) + "\n\n";
      if (courseOutline.courseInfo.title) {
        text += `${courseOutline.courseInfo.title}\n\n`;
      }
      if (courseOutline.courseInfo.description) {
        text += `${courseOutline.courseInfo.description}\n\n`;
      }
    }

    // Sections
    if (courseOutline.sections && courseOutline.sections.length > 0) {
      text += `\n${i18n.t("AudioPanel.Sections")}\n`;
      text += "=".repeat(50) + "\n\n";

      courseOutline.sections.forEach((section, index) => {
        text += `${index + 1}. ${section.title}\n`;
        text += `   (${section.allocatedScenes} ${i18n.t("AudioPanel.AllocatedScenes")})\n\n`;

        if (section.relevantDocumentPart?.key_information) {
          section.relevantDocumentPart.key_information.forEach((info) => {
            text += `   • ${info}\n`;
          });
          text += "\n";
        }
      });
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#0f172a",
          }}
        >
          {i18n.t("AudioPanel.DocumentTitles")}
        </div>
        {courseOutline && (
          <button
            type="button"
            onClick={copyToClipboard}
            style={{
              fontSize: 11,
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #E5E7EB",
              backgroundColor: copied ? "#10B981" : "#F9FAFB",
              color: copied ? "#FFFFFF" : "#6B7280",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            title={i18n.t("AudioPanel.CopyAll")}
          >
            {copied ? i18n.t("AudioPanel.Copied") : i18n.t("AudioPanel.CopyAll")}
          </button>
        )}
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#8F8F8F",
              fontSize: "13px",
              padding: "20px",
            }}
          >
            {i18n.t("AudioPanel.Loading")}
          </div>
        )}
        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#EF4444",
              fontSize: "13px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            {i18n.t("AudioPanel.ErrorLoadingData")}
          </div>
        )}
        {!loading && !error && courseOutline && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "8px",
            }}
          >
            {/* Course Info */}
            {courseOutline.courseInfo && (
              <div
                style={{
                  padding: "8px",
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  {i18n.t("AudioPanel.CourseInfo")}
                </div>
                {courseOutline.courseInfo.title && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#374151",
                      marginBottom: "6px",
                    }}
                  >
                    {courseOutline.courseInfo.title}
                  </div>
                )}
                {courseOutline.courseInfo.description && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#374151",
                      lineHeight: "1.6",
                    }}
                  >
                    {courseOutline.courseInfo.description}
                  </div>
                )}
              </div>
            )}

            {/* Sections */}
            {courseOutline.sections && courseOutline.sections.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#111827",
                    marginTop: "8px",
                  }}
                >
                  {i18n.t("AudioPanel.Sections")}
                </div>
                {courseOutline.sections.map((section, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "8px",
                      background: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: "#111827",
                        marginBottom: "4px",
                      }}
                    >
                      {index + 1}. {section.title}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6B7280",
                        marginBottom: "8px",
                      }}
                    >
                      {section.allocatedScenes} {i18n.t("AudioPanel.AllocatedScenes")}
                    </div>
                    {section.relevantDocumentPart?.key_information &&
                      section.relevantDocumentPart.key_information.length > 0 && (
                        <ul
                          style={{
                            margin: "4px 0 0 0",
                            paddingLeft: "20px",
                            fontSize: 11,
                            color: "#374151",
                            lineHeight: "1.6",
                          }}
                        >
                          {section.relevantDocumentPart.key_information.map((info, idx) => (
                            <li key={idx} style={{ marginBottom: "4px" }}>
                              {info}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
        {!loading && !error && !courseOutline && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#8F8F8F",
              fontSize: "13px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            {i18n.t("AudioPanel.NoOutlineAvailable")}
          </div>
        )}
      </div>
    </div>
  );
};

OutlinePanel.propTypes = {
  baseUrl: PropTypes.string,
  trainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default OutlinePanel;
